/**
 * The second factor, as a form: where a token from the re-auth page is brought
 * back and spent.
 *
 * TWO SCREENS NEED IT, and it lived inside one of them. The review modal needs
 * it for a config change the warden marks `needsReauth`; the Maintenance tab
 * needs it for a container verb the warden answers `reauth_required`. What
 * happens AFTER the grant is each caller's own and deliberately different — the
 * modal asks for the plan again, the maintenance card waits for a second press —
 * so this spends the token, says when the grant runs out, and does nothing else.
 *
 * WHY IT IS MANUAL, since a form that asks somebody to paste a token reads like
 * an oversight: the Server screen reaches the warden over the tailnet, where a
 * request carries a Tailscale identity and no Access token, so the proof of a
 * fresh sign-in cannot come off the request. `apps/mailgw/src/auth/reauth.ts`
 * sets that out in full.
 *
 * ONE SUBMIT AT A TIME, guarded by a ref. The button is disabled while a token
 * is being checked, but `setState` is not readable on the next line, and a
 * second submit in the same burst would spend the token twice.
 */
import { useRef, useState } from 'react'
import { api, REAUTH_PATH } from './api.ts'

export interface ReauthFormProps {
  /** Why the server wants it, in the words of whatever asked. A whole sentence. */
  readonly because: string
  /**
   * The origin agreed. `expiresAt` is Unix SECONDS on the origin's clock — the
   * one that decides, which is not this browser's.
   */
  readonly onGranted: (expiresAt: number) => void | Promise<void>
  /** The origin refused the token. The form stays where it is, which is the truth. */
  readonly onRefused: (error: unknown) => void
}

export function ReauthForm({ because, onGranted, onRefused }: ReauthFormProps): React.JSX.Element {
  const [token, setToken] = useState('')
  const [checking, setChecking] = useState(false)
  const inFlight = useRef(false)

  const submit = (): void => {
    const value = token.trim()
    if (value === '' || inFlight.current) return
    inFlight.current = true
    setChecking(true)
    api
      .reauth(value)
      .then((granted) => {
        // CLEARED ON SUCCESS. It is a credential, it has been spent, and leaving
        // it in a textarea somebody may walk away from buys nothing.
        setToken('')
        return onGranted(granted.expiresAt)
      })
      .catch(onRefused)
      .finally(() => {
        inFlight.current = false
        setChecking(false)
      })
  }

  return (
    <form
      className="admin-reauth"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <h4 className="admin-form-title">Sign in again</h4>
      <p className="admin-note">
        {because} The server wants proof from the last few minutes — not the session this tab is already using.
        Open <code className="admin-reauth-path">{REAUTH_PATH}</code> on the address you use for Hearth: it will ask
        who you are again and print a token. Paste it here.
      </p>
      <label className="admin-field">
        <span>The token that page printed</span>
        <textarea
          className="admin-reauth-token"
          value={token}
          onChange={(event) => setToken(event.target.value)}
          spellCheck={false}
          autoComplete="off"
          rows={3}
        />
      </label>
      {/* DISABLED WHILE EMPTY, because the only thing an empty submit could
          produce is the origin's `bad_request` — a round trip to be told what
          this side already knows. */}
      <button type="submit" className="admin-apply" disabled={checking || token.trim() === ''}>
        {checking ? 'Checking…' : 'I have signed in again'}
      </button>
    </form>
  )
}
